import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { throwError } from 'rxjs/internal/observable/throwError';
import { Subject } from 'rxjs/internal/Subject';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthTokenInfo } from '../../../core/contracts/auth-token-info';
import { Credentials } from '../../../core/contracts/credentials';
import { AccessDeniedError, LoginOrPasswordError } from '../../../core/contracts/errors';
import { UserAccount } from '../../../core/contracts/user-account';
import { UserRoles } from '../../../core/user-roles';
import { combineUrl } from '../../../core/utils';
import { UserPermissions } from '../../constants/permissions';
import { UserRolesApiService } from "../../api/services/user-roles-api.service";

const AUTH_SCOPE = 'all';
const JWT_TOKEN = 'JWT_TOKEN';
const REFRESH_TOKEN = 'REFRESH_TOKEN';
const ACCESS_TOKEN = 'ACCESS_TOKEN';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  readonly onLoggedIn = new Subject<void>();
  private accessToken: AuthTokenInfo;
  private userRolesApiService = inject(UserRolesApiService)
  private http = inject(HttpClient)

  get user(): UserAccount {
    return this.accessToken.user;
  }

  get isLoggedIn(): boolean {
    return !!this.accessToken;
  }

  constructor() {
    this.accessToken = this.loadTokens();
  }

  async login(credentials: Credentials): Promise<boolean> {
    this.accessToken = await this.getAccessToken(credentials);
    this.storeTokens(this.accessToken);
    this.onLoggedIn.next();
    return true;
  }

  logout() {
    this.accessToken = null;
    this.removeTokens();
  }

  getJwtToken() {
    return this.accessToken.accessToken;
  }

  async init(): Promise<boolean> {
    if ( this.accessToken ) {
      try {
        if ( dayjs(this.accessToken.refreshTokenExpiresAt).isAfter(dayjs()) ) {
          this.accessToken = await this.refreshAccessToken(this.accessToken.refreshToken);
          this.storeTokens(this.accessToken);
        } else {
          this.logout();
        }
      } catch {
        this.logout();
      }
    }
    return true;
  }

  hasProjectRole(role: UserRoles): boolean {
    return this.user && this.user.projects && this.user.projects.some(t => t.role && t.role.indexOf(role) > -1);
  }


  hasRole(role: number): boolean  {
    return this.user && this.user.role_id && this.user.role_id == role
  }

  hasAnyRole(roles: number[]): boolean {
    return roles.some(r => this.hasRole(r));
  }


  hasAnyProjectRole(roles: UserRoles[]): boolean {
    return roles.some(r => this.hasProjectRole(r));
  }

  hasPermission(permission: string | UserPermissions): boolean {
    return this.user.permissions.indexOf(permission) > -1;
  }

  private storeTokens(tokens: AuthTokenInfo) {
    localStorage.setItem(ACCESS_TOKEN, JSON.stringify(tokens));
  }

  private loadTokens(): AuthTokenInfo | null {
    const str = localStorage.getItem(ACCESS_TOKEN);
    if ( str ) {
      const obj = JSON.parse(str);
      obj.accessTokenExpiresAt = new Date(obj.accessTokenExpiresAt);
      obj.refreshTokenExpiresAt = new Date(obj.refreshTokenExpiresAt);
      return obj;
    }
    return null;
  }

  private removeTokens() {
    localStorage.removeItem(ACCESS_TOKEN);
  }

  private refreshAccessToken(refreshToken: string): Promise<AuthTokenInfo> {
    const url = combineUrl(environment.host, 'auth', 'token');
    const data = `refresh_token=${refreshToken}&grant_type=refresh_token`;
    return this.http.post<any>(url, data, {
      headers: {
        'Authorization': `Basic ${btoa(environment.appClientId + ':' + AUTH_SCOPE)}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      observe: 'body',
      responseType: 'json'
    }).pipe(
      map(body => ({
        ...body,
        accessTokenExpiresAt: new Date(body.accessTokenExpiresAt),
        refreshTokenExpiresAt: new Date(body.refreshTokenExpiresAt)
      })),
    ).toPromise();
  }

  private getAccessToken(credentials: Credentials): Promise<AuthTokenInfo> {
    const url = combineUrl(environment.host, 'auth', 'token');
    const data = `username=${credentials.username}&password=${credentials.password}&grant_type=password&scope=${AUTH_SCOPE}`;
    return this.http.post<any>(url, data, {
      headers: {
        'Authorization': `Basic ${btoa(environment.appClientId + ':' + AUTH_SCOPE)}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      observe: 'body',
      responseType: 'json'
    }).pipe(
      map(body => ({
        ...body,
        accessTokenExpiresAt: new Date(body.accessTokenExpiresAt),
        refreshTokenExpiresAt: new Date(body.refreshTokenExpiresAt)
      })),
      catchError(error => {
        if ( error.status == 400 ) {
          if ( error.error.name == 'user_locked' ) {
            return throwError(new AccessDeniedError());
          } else if ( error.error.name == 'invalid_password' || error.error.name == 'user_not_found' ) {
            return throwError(new LoginOrPasswordError());
          }
        }
        return throwError(error);
      })
    ).toPromise();
  }
}
